import React, { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';

const KEY = 'kf_theme';

function readInitial() {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch (_) { /* ignore */ }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

function apply(theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.style.colorScheme = theme;
}

/* Apply the theme on first script execution — before React mounts — so
   we don't get a white flash on a dark-preference user's initial paint. */
apply(readInitial());

export function useTheme() {
  const [theme, setTheme] = useState(readInitial);
  useEffect(() => {
    apply(theme);
    try { localStorage.setItem(KEY, theme); } catch (_) { /* ignore */ }
    // Other components can react to a theme change (e.g. Wingman's iframe).
    window.dispatchEvent(new CustomEvent('kf:theme-change', { detail: theme }));
  }, [theme]);
  return [theme, setTheme];
}

/* Compact icon button — fits the TopBar utility cluster. */
export default function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={() => setTheme(next)}
      className="h-9 w-9 rounded-md hover:bg-neutral-100 flex items-center justify-center text-neutral-500 hover:text-neutral-900 transition"
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
    >
      {theme === 'dark'
        ? <Sun className="h-[17px] w-[17px]" />
        : <Moon className="h-[17px] w-[17px]" />}
    </button>
  );
}
